import { areAdsEnabled, createAdSlot } from "./ads/ad_slots";

export type OverlayHandle = {
  element: HTMLElement;
  destroy: () => void;
};

export type QueueOverlayHandle = OverlayHandle & {
  setMessage: (message: string) => void;
};

export type DefeatOverlayOptions = {
  title: string;
  subtitle: string;
  respawnText: string;
  menuText: string;
  timeoutMs?: number;
  onRespawn: () => void;
  onMenu: () => void;
  onTimeout?: () => void;
};

export type PauseOverlayOptions = {
  title: string;
  resumeText: string;
  menuText: string;
  onResume: () => void;
  onMenu: () => void;
};

export type QueueOverlayOptions = {
  title: string;
  message: string;
  menuText: string;
  onMenu: () => void;
};

export {
  createUpgradeOverlay,
  type UpgradeOverlayOptions
} from "./upgrade_overlays";

export function createDefeatOverlay(options: DefeatOverlayOptions): OverlayHandle {
  const adsEnabled = areAdsEnabled();
  const overlay = document.createElement("div");
  overlay.className = "defeat-modal-backdrop";

  const modal = document.createElement("div");
  modal.className = adsEnabled ? "defeat-modal defeat-modal-locked" : "defeat-modal";

  const header = document.createElement("div");
  header.className = "defeat-modal-header";

  const title = document.createElement("h2");
  title.className = "defeat-modal-title";
  title.textContent = options.title;

  const countdown = document.createElement("div");
  countdown.className = "upgrade-countdown defeat-countdown";

  header.append(title);

  const subtitle = document.createElement("p");
  subtitle.className = "defeat-modal-subtitle";
  subtitle.textContent = options.subtitle;

  const actions = document.createElement("div");
  actions.className = "defeat-modal-actions";

  const respawn = document.createElement("button");
  respawn.type = "button";
  respawn.className = adsEnabled
    ? "ui-button ui-button-primary ui-button-disabled"
    : "ui-button ui-button-primary";
  respawn.textContent = options.respawnText;
  respawn.disabled = adsEnabled;

  const menu = document.createElement("button");
  menu.type = "button";
  menu.className = adsEnabled
    ? "ui-button ui-button-secondary ui-button-disabled"
    : "ui-button ui-button-secondary";
  menu.textContent = options.menuText;
  menu.disabled = adsEnabled;

  actions.append(respawn, menu);
  modal.append(header, subtitle, actions);

  let closed = false;
  let countdownIntervalId: number | null = null;
  let timeoutId: number | null = null;
  const hasTimeout =
    typeof options.timeoutMs === "number" &&
    Number.isFinite(options.timeoutMs) &&
    options.timeoutMs > 0;
  const timeoutMs = hasTimeout ? Math.max(1, Math.round(options.timeoutMs ?? 0)) : 0;
  const deadline = Date.now() + timeoutMs;

  const clearTimers = () => {
    if (countdownIntervalId !== null) {
      window.clearInterval(countdownIntervalId);
      countdownIntervalId = null;
    }

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const runAction = (action: () => void) => {
    if (closed) {
      return;
    }

    closed = true;
    clearTimers();
    action();
  };

  const updateCountdown = () => {
    const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    countdown.textContent = `${secondsLeft}s`;
  };

  respawn.addEventListener("click", () => runAction(options.onRespawn));
  menu.addEventListener("click", () => runAction(options.onMenu));

  if (hasTimeout) {
    header.append(countdown);
    updateCountdown();
    countdownIntervalId = window.setInterval(updateCountdown, 200);

    if (options.onTimeout) {
      const onTimeout = options.onTimeout;
      timeoutId = window.setTimeout(() => runAction(onTimeout), timeoutMs);
    }
  }

  if (!adsEnabled) {
    overlay.append(modal);

    return {
      element: overlay,
      destroy: () => {
        closed = true;
        clearTimers();
        overlay.remove();
      }
    };
  }

  const topAd = createAdSlot("defeat-top", "ad-slot-defeat-top");
  const leftAd = createAdSlot("defeat-left", "ad-slot-defeat-side ad-slot-defeat-left");
  const rightAd = createAdSlot("defeat-right", "ad-slot-defeat-side ad-slot-defeat-right");
  const bottomAd = createAdSlot("defeat-bottom", "ad-slot-defeat-bottom");

  const centerAdShell = document.createElement("div");
  centerAdShell.className = "defeat-center-ad-shell";

  const centerAd = createAdSlot("defeat-center", "ad-slot-defeat-center");

  const closeAd = document.createElement("button");
  closeAd.type = "button";
  closeAd.className = "defeat-center-ad-close";
  closeAd.textContent = "×";
  closeAd.setAttribute("aria-label", "Close advertisement");
  closeAd.addEventListener("click", () => {
    centerAdShell.remove();
    modal.classList.remove("defeat-modal-locked");
    respawn.disabled = false;
    menu.disabled = false;
    respawn.classList.remove("ui-button-disabled");
    menu.classList.remove("ui-button-disabled");
    respawn.focus();
  });

  centerAdShell.append(centerAd, closeAd);
  overlay.append(topAd, leftAd, rightAd, bottomAd, modal, centerAdShell);

  return {
    element: overlay,
    destroy: () => {
      closed = true;
      clearTimers();
      overlay.remove();
    }
  };
}

export function createPauseOverlay(options: PauseOverlayOptions): OverlayHandle {
  const overlay = document.createElement("div");
  overlay.className = "pause-modal-backdrop";

  const modal = document.createElement("div");
  modal.className = "pause-modal";

  const title = document.createElement("h2");
  title.className = "pause-modal-title";
  title.textContent = options.title;

  const actions = document.createElement("div");
  actions.className = "pause-modal-actions";

  const resume = document.createElement("button");
  resume.type = "button";
  resume.className = "ui-button ui-button-primary";
  resume.textContent = options.resumeText;
  resume.addEventListener("click", options.onResume);

  const menu = document.createElement("button");
  menu.type = "button";
  menu.className = "ui-button ui-button-secondary";
  menu.textContent = options.menuText;
  menu.addEventListener("click", options.onMenu);

  actions.append(resume, menu);
  modal.append(title, actions);
  overlay.append(modal);

  return {
    element: overlay,
    destroy: () => overlay.remove()
  };
}

export function createQueueOverlay(options: QueueOverlayOptions): QueueOverlayHandle {
  const adsEnabled = areAdsEnabled();
  const overlay = document.createElement("div");
  overlay.className = "pause-modal-backdrop queue-modal-backdrop";

  const modal = document.createElement("div");
  modal.className = "pause-modal queue-modal";

  const title = document.createElement("h2");
  title.className = "pause-modal-title queue-modal-title";
  title.textContent = options.title;

  const message = document.createElement("p");
  message.className = "defeat-modal-subtitle queue-modal-message";
  message.textContent = options.message;

  const actions = document.createElement("div");
  actions.className = "pause-modal-actions";

  const menu = document.createElement("button");
  menu.type = "button";
  menu.className = "ui-button ui-button-secondary";
  menu.textContent = options.menuText;
  menu.addEventListener("click", options.onMenu);

  actions.append(menu);
  modal.append(title, message, actions);

  if (!adsEnabled) {
    overlay.append(modal);

    return {
      element: overlay,
      setMessage: (nextMessage: string) => {
        message.textContent = nextMessage;
      },
      destroy: () => overlay.remove()
    };
  }

  const topAd = createAdSlot("queue-top", "ad-slot-defeat-top");
  const leftAd = createAdSlot("queue-left", "ad-slot-defeat-side ad-slot-defeat-left");
  const rightAd = createAdSlot("queue-right", "ad-slot-defeat-side ad-slot-defeat-right");
  const bottomAd = createAdSlot("queue-bottom", "ad-slot-defeat-bottom");

  overlay.append(topAd, leftAd, rightAd, bottomAd, modal);

  return {
    element: overlay,
    setMessage: (nextMessage: string) => {
      message.textContent = nextMessage;
    },
    destroy: () => overlay.remove()
  };
}